import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { fileSyncService } from '../services/fileSyncService';
import { toast } from 'sonner';
import { 
  FolderSync, FolderOpen, RefreshCw, CheckCircle, 
  AlertCircle, Loader2, Upload, Download, X 
} from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FileSyncWidget = ({ onDocumentUploaded }) => {
  const { token } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [status, setStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      setIsSupported(fileSyncService.isSupported());
      
      if (fileSyncService.isSupported()) {
        // Try to restore previously selected directory
        const dirName = await fileSyncService.restoreDirectory();
        if (dirName) {
          setStatus(fileSyncService.getStatus());
          
          // Start watching for new files
          fileSyncService.watchInbox(token, API_URL, (result) => {
            if (result.success) {
              toast.success(`${result.name} hochgeladen`, {
                description: result.sender ? `Erkannt: ${result.sender}` : undefined
              });
              if (onDocumentUploaded) onDocumentUploaded();
            } else {
              toast.error(`Fehler: ${result.name}`, {
                description: result.error
              });
            }
          });
        }
      }
    };
    
    init();
    
    return () => {
      fileSyncService.stopWatching();
    };
  }, [token, onDocumentUploaded]);

  // Select folder
  const handleSelectFolder = async () => {
    try {
      const dirName = await fileSyncService.selectDirectory();
      if (dirName) {
        setStatus(fileSyncService.getStatus());
        toast.success('Sync-Ordner ausgewählt', {
          description: `${dirName} wird jetzt überwacht`
        });
        
        // Start watching
        fileSyncService.watchInbox(token, API_URL, (result) => {
          if (result.success) {
            toast.success(`${result.name} hochgeladen`);
            if (onDocumentUploaded) onDocumentUploaded();
          }
        });
        
        // Initial sync
        handleSync();
      }
    } catch (error) {
      toast.error('Fehler', { description: error.message });
    }
  };

  // Sync from server
  const handleSync = async () => {
    if (!status?.hasDirectory) return;
    
    setIsSyncing(true);
    setSyncProgress(null);
    
    try {
      const result = await fileSyncService.syncFromServer(
        token, 
        API_URL,
        (progress) => setSyncProgress(progress)
      );
      
      setLastResult(result);
      setStatus(fileSyncService.getStatus());
      
      if (result.downloaded > 0) {
        toast.success(`${result.downloaded} Dokument(e) synchronisiert`, {
          description: `In deinem Ordner: ${status.directoryName}`
        });
      } else if (result.skipped === result.total) {
        toast.info('Alles synchron', {
          description: 'Keine neuen Dokumente'
        });
      }
    } catch (error) {
      toast.error('Sync-Fehler', { description: error.message });
    } finally {
      setIsSyncing(false);
      setSyncProgress(null);
    }
  };

  // Disconnect folder
  const handleDisconnect = async () => {
    fileSyncService.stopWatching();
    await fileSyncService.clearSyncData();
    setStatus(null);
    setLastResult(null);
    toast.info('Sync-Ordner getrennt');
  };

  if (!isSupported) {
    return (
      <div className="glass-card rounded-2xl p-4 border border-amber-500/30 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-500 text-sm">Sync nicht verfügbar</p>
            <p className="text-xs text-amber-500/70 mt-1">
              Öffne JaySmart in Chrome oder Edge für automatische Ordner-Synchronisation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!status?.hasDirectory) {
    return (
      <div className="glass-card rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <FolderSync className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="font-medium text-foreground text-sm">Lokaler Ordner</p>
              <p className="text-xs text-muted-foreground">Dokumente automatisch speichern</p>
            </div>
          </div>
          <Button
            onClick={handleSelectFolder}
            size="sm"
            className="bg-violet-500 hover:bg-violet-600"
            data-testid="select-sync-folder-btn"
          >
            <FolderOpen className="w-4 h-4 mr-1" />
            Verbinden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="font-medium text-foreground text-sm flex items-center gap-2">
              {status.directoryName}
              {status.isWatching && (
                <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">
                  Aktiv
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {status.syncedCount} Dokumente synchronisiert
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSync}
            disabled={isSyncing}
            size="sm"
            variant="outline"
            className="h-8"
          >
            {isSyncing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
          <Button
            onClick={handleDisconnect}
            size="sm"
            variant="ghost"
            className="h-8 text-muted-foreground hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Sync Progress */}
      {syncProgress && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span className="truncate max-w-[200px]">{syncProgress.currentFile}</span>
            <span>{syncProgress.current}/{syncProgress.total}</span>
          </div>
          <Progress value={(syncProgress.current / syncProgress.total) * 100} className="h-1" />
        </div>
      )}

      {/* Last Result */}
      {lastResult && !isSyncing && (
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Download className="w-3 h-3 text-emerald-400" />
            {lastResult.downloaded} neu
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            {lastResult.skipped} bereits synchron
          </span>
        </div>
      )}

      {/* Hint */}
      <div className="mt-3 text-[10px] text-muted-foreground/70 flex items-center gap-1">
        <Upload className="w-3 h-3" />
        Lege Dateien in "_Eingang" für automatischen Upload
      </div>
    </div>
  );
};

export default FileSyncWidget;
